import type { I18nText } from "./locales";
/**
 * 服务端配置 ↔ 前端模型的映射。
 *
 * 两边刻意不是同一个形状：后端按表存（cfg_main_city / building / cell），
 * 前端按编辑心智组织（Level 里网格、竞品、i18n 揉在一起）。映射规则集中在
 * 本文件，两边谁演进都只改这里。
 *
 * 后端还没有的字段在注释里点名，读进来给默认值、写回去时跳过：
 *   主建筑标记 —— cfg_main_building 无 main 列（前端约定最后一栋占位）
 *   竞品网格   —— 走 ref_dataset，另一条读链路
 */
import type {
  LevelPatchBody, NewLevelBody, ServerAchievement, ServerActItem, ServerCommon, ServerLevel, ServerPack, ServerPeriod, ServerReward, ServerTitle, VersionInfo,
} from "./api";
import { DEFAULT_DICE, DEFAULT_FIGHT, DEFAULT_INTIMACY, DEFAULT_LAMP, DEFAULT_PET_STAR, DEFAULT_PETTOWN, type DiceParams, type FightParams, type IntimacyParams, type LampParams, type PetStarParams, type PettownParams } from "./params.ts";
import type { Reward, RewardItem, RewardKind } from "./reward";
import type { Achievement, ActItem, Title } from "./achievement";
import type { Card, CardPack } from "./card";
import type { AuctionMap, AuctionPeriod } from "./auction";
// 显式带 .ts —— node 的 ESM 解析不做扩展名补全，不写就跑不了单元测试（vite 两种都认）
import { cellKey, type Grid, type Level } from "./model.ts";
import { fromBig, money, toBig, type Money } from "./big.ts";
import type { Version } from "./version";
import { asRefreshMode } from "./refresh-mode.ts";

/** 后端版本号是数字，前端沿用原型的 v128 风格展示 */
export const vid = (n: number) => `v${n}`;
export const vnum = (id: string): number => Number(id.replace(/^v/, ""));

/**
 * 配置台首次打开只自动进入比生效版本新的草稿，否则展示生效版本。
 *
 * 草稿可能是在更早的线上版本上创建的；默认落进去会让运营误以为自己正在修改
 * 当前线上配置。没有生效版本（新代理首次配置）时打开草稿或列表第一项。
 */
export function initialVersion(rows: VersionInfo[]): VersionInfo | undefined {
  const active = rows.find((r) => r.active);
  return rows.find((r) => r.status === "draft" && !r.active && (!active || r.configVersion > active.configVersion))
    ?? active
    ?? rows[0];
}

export function toVersion(row: VersionInfo): Version {
  const d = new Date(row.createdAtMs);
  const p = (n: number) => String(n).padStart(2, "0");
  return {
    id: vid(row.configVersion),
    // 生效与否是 cfg_active 的事实，不是 status 列的取值——active 标志优先
    status: row.active ? "active" : row.status === "draft" ? "draft" : "published",
    time: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`,
    user: row.author,
    note: row.note ?? "",
    checksum: row.checksum,
    from: row.rollbackFrom == null ? null : vid(row.rollbackFrom),
    base: row.baseVersion == null ? null : vid(row.baseVersion),
    updatedBy: row.updatedBy ?? "",
    updatedAtMs: row.updatedAtMs ?? null,
    refreshMode: asRefreshMode(row.lastRefreshMode),
    economyModel: row.economyModel === 2 ? 2 : 1,
    moneyScale: row.moneyScaleNum && row.moneyScaleDen ? { num: row.moneyScaleNum, den: row.moneyScaleDen } : null,
    legacyEMilli: row.legacyEMilli ?? null,
    publishedBy: row.publishedBy ?? "",
    publishedAtMs: row.publishedAtMs ?? null,
    people: row.contributors ?? [],
  };
}

export function toLevel(sl: ServerLevel): Level {
  const N = sl.nBuildings, M = sl.nLevels;
  // 残缺格后端存 0（读侧不掩盖，发布校验拦）；编辑页把 0 当「待填」展示
  const sys: Grid = {};
  if (sl.cells) {
    for (let b = 1; b <= N; b++)
      for (let l = 1; l <= M; l++) {
        // DECIMAL 列按字符串下发：先规范成 Money，"0" 与 0 一样是待填
        const v = money(sl.cells[b - 1]?.[l - 1]);
        sys[cellKey(b, l)] = v == null || v === 0 ? null : v;
      }
  }
  const i18n: Level["i18n"] = {};
  for (const [code, name] of Object.entries(sl.nameI18n ?? {})) {
    if (code !== "en" && name) i18n[code] = { name };
  }
  for (const [code, stage] of Object.entries(sl.stageI18n ?? {})) {
    if (code !== "en" && stage) i18n[code] = { ...i18n[code], stage };
  }
  const buildings = Array.from({ length: N }, (_, i) => {
    const b = sl.buildings.find((x) => x.idx === i + 1);
    return {
      code: `${sl.level}-building-${i + 1}`,
      name: b?.name ?? `Building ${i + 1}`,
      nameI18n: b?.nameI18n ?? {},
      main: b?.main ?? false,
      x: b?.offsetX ?? 0,
      y: b?.offsetY ?? 0,
    };
  });
  // 老数据没标主建筑时前端用「最后一栋」占位，保存时会把真值写回去
  if (!buildings.some((b) => b.main) && buildings.length) buildings[buildings.length - 1].main = true;
  return {
    id: sl.level,
    // 老服务端 / 没回填到的行没有 uid：按位置兜底（只在本次会话内稳定，迁移跑过之后不会出现）
    uid: sl.uid || `L${sl.level}`,
    name: sl.name,
    stage: sl.stage ?? "",
    i18n,
    B: money(sl.bValue),
    targetRolls: money(sl.targetRolls),
    targetRollsOverride: money(sl.targetRollsOverride),
    gMilli: sl.gMilli ?? null,
    costMatrixSeed: sl.costMatrixSeed ?? null,
    rollsRange: sl.rollsRangePct ?? null,
    actualC: money(sl.actualC),
    coverImg: sl.coverImg ?? "",
    bgImg: sl.bgImg ?? "",
    N,
    M,
    buildings,
    // bld_init 列是权威；老数据没有时回退逐格 1★ 价，仍没有则留空待填
    bldInit: Array.from({ length: N }, (_, b) => {
      const v = money(sl.bldInit?.[b] ?? sl.cells?.[b]?.[0]);
      return v == null || v === 0 ? null : v;
    }),
    star: sl.starShapeBps.map((v) => v / 10000),
    sys,
    // 服务端按 `"<建筑>:<星级>"` 返回，前端内部一律用 cellKey（`b_l`）。
    // 两种键格式共存过一阵：AssetDialog 用冒号能对上，而任何拿 cellKey 去查
    // assets 的地方永远取不到——在这里转一次，让前端只有一种键。
    assets: Object.fromEntries(
      Object.entries(sl.assets ?? {}).map(([k, v]) => {
        const [b, l] = k.split(":").map(Number);
        return [cellKey(b, l), v] as const;
      }),
    ),
    levelAssets: sl.levelAssets ?? {},
    rivals: {},
  };
}

export const toLevels = (levels: ServerLevel[]): Level[] => levels.map(toLevel);

/* ── 写方向：Level 差异 → LevelPatch ── */

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** sys 网格 → 逐格 cells。待填格（null）不下发——replace_all 语义下缺席即空 */
export function toCells(lv: Level): Array<{ buildingIdx: number; starLevel: number; cost: Money }> {
  const out: Array<{ buildingIdx: number; starLevel: number; cost: Money }> = [];
  for (let b = 1; b <= lv.N; b++)
    for (let l = 1; l <= lv.M; l++) {
      const v = lv.sys[cellKey(b, l)];
      if (v != null) out.push({ buildingIdx: b, starLevel: l, cost: v });
    }
  return out;
}

const nameI18nOf = (lv: Level): Record<string, string> => ({
  en: lv.name,
  ...Object.fromEntries(Object.entries(lv.i18n).flatMap(([c, v]) => (v.name ? [[c, v.name]] : []))),
});
const stageI18nOf = (lv: Level): Record<string, string> => ({
  en: lv.stage,
  ...Object.fromEntries(Object.entries(lv.i18n).flatMap(([c, v]) => (v.stage ? [[c, v.stage]] : []))),
});

/**
 * 逐字段差异补丁。base 是服务端当前内容，next 是编辑后的关卡；
 * 没有差异返回 null。改 N/M 时按后端要求连全量 cells 一起带上。
 */
export function diffToPatch(base: Level, next: Level): LevelPatchBody | null {
  const p: LevelPatchBody = {};
  if ((next.costMatrixSeed ?? null) !== (base.costMatrixSeed ?? null)) p.costMatrixSeed = next.costMatrixSeed ?? null;
  if (next.B != null && next.B !== base.B) p.bValue = next.B;
  if (next.targetRolls != null && next.targetRolls !== base.targetRolls) p.targetRolls = next.targetRolls;
  if ((next.targetRollsOverride ?? null) !== (base.targetRollsOverride ?? null)) p.targetRollsOverride = next.targetRollsOverride ?? null;
  if (next.rollsRange != null && next.rollsRange !== base.rollsRange) p.rollsRangePct = next.rollsRange;
  if (next.actualC !== base.actualC) p.actualC = next.actualC;
  // g 的 null 是「回到曲线」，是真值：看变没变，不看空不空（与上面「留空沿用基线」的几项不同）
  if ((next.gMilli ?? null) !== (base.gMilli ?? null)) p.gMilli = next.gMilli ?? null;
  if (next.coverImg !== base.coverImg) p.coverImg = next.coverImg;
  if (next.bgImg !== base.bgImg) p.bgImg = next.bgImg;
  if (!sameJson(next.star, base.star)) p.starShapeBps = next.star.map((v) => Math.round((v ?? 0) * 10000));
  if (!sameJson(next.bldInit, base.bldInit)) p.bldInit = next.bldInit;
  if (next.name !== base.name || !sameJson(nameI18nOf(next), nameI18nOf(base))) p.nameI18n = nameI18nOf(next);
  if (next.stage !== base.stage || !sameJson(stageI18nOf(next), stageI18nOf(base))) p.stageI18n = stageI18nOf(next);
  const dimChange = next.N !== base.N || next.M !== base.M;
  if (dimChange) { p.nBuildings = next.N; p.nLevels = next.M; }
  if (dimChange || !sameJson(next.sys, base.sys)) p.cells = toCells(next);
  if (!sameJson(next.levelAssets ?? {}, base.levelAssets ?? {})) p.levelAssets = next.levelAssets ?? {};
  const bldSig = (lv: Level) => lv.buildings.map((b) => `${b.name}|${b.main ? 1 : 0}|${b.x}|${b.y}`);
  if (!sameJson(bldSig(next), bldSig(base)) || dimChange) {
    p.buildings = next.buildings.map((b, i) => ({ idx: i + 1, name: b.name, main: b.main, offsetX: b.x, offsetY: b.y }));
  }
  return Object.keys(p).length ? p : null;
}

/** 新增关卡的创建体。数值待填项不下发，后端存 0 = 待填 */
export function toNewLevel(lv: Level): NewLevelBody {
  return {
    uid: lv.uid,
    costMatrixSeed: lv.costMatrixSeed ?? 0,
    nameI18n: nameI18nOf(lv),
    ...(lv.stage || Object.values(lv.i18n).some((v) => v.stage) ? { stageI18n: stageI18nOf(lv) } : {}),
    ...(lv.B != null ? { bValue: lv.B } : {}),
    ...(lv.targetRolls != null ? { targetRolls: lv.targetRolls } : {}),
    ...(lv.targetRollsOverride != null ? { targetRollsOverride: lv.targetRollsOverride } : {}),
    ...(lv.gMilli != null ? { gMilli: lv.gMilli } : {}),
    ...(lv.rollsRange != null ? { rollsRangePct: lv.rollsRange } : {}),
    nBuildings: lv.N,
    nLevels: lv.M,
    // 待填的星形状按 0 占位，长度必须 = nLevels
    starShapeBps: Array.from({ length: lv.M }, (_, i) => Math.round((lv.star[i] ?? 0) * 10000)),
    ...(lv.bldInit.some((v) => v != null) ? { bldInit: lv.bldInit } : {}),
    buildings: lv.buildings.map((b, i) => ({ idx: i + 1, name: b.name, main: b.main })),
    ...(Object.keys(lv.sys).length ? { cells: toCells(lv) } : {}),
  };
}

/**
 * 竞品逐格价（服务端稀疏行）挂进各关的 levels[].rivals[列 id]。
 * 没填的格不出现在行里，网格里也就没有那个键——与我方 sys 网格「待填 = 缺键」同一口径。
 */
export function attachRivalGrids(
  levels: Level[], rows: Array<{ rivalId: string; level: number; buildingIdx: number; starLevel: number; cost: Money }>,
): Level[] {
  const by = new Map<number, Record<string, Grid>>();
  for (const r of rows) {
    const lv = by.get(r.level) ?? {};
    (lv[r.rivalId] ??= {})[cellKey(r.buildingIdx, r.starLevel)] = money(r.cost);
    by.set(r.level, lv);
  }
  return levels.map((l) => ({ ...l, rivals: by.get(l.id) ?? {} }));
}

/** 某一列在某关的逐格价 → 服务端行。待填格（无键 / null）不下发 */
export function rivalCellsOf(lv: Level, rivalId: string): Array<{ level: number; buildingIdx: number; starLevel: number; cost: Money }> {
  const grid = lv.rivals[rivalId] ?? {};
  const out: Array<{ level: number; buildingIdx: number; starLevel: number; cost: Money }> = [];
  for (let b = 1; b <= lv.N; b++)
    for (let l = 1; l <= lv.M; l++) {
      const v = grid[cellKey(b, l)];
      if (v != null) out.push({ level: lv.id, buildingIdx: b, starLevel: l, cost: v });
    }
  return out;
}

/**
 * 某一列的逐格价在哪些关被人改了，按关卡 uid 与基线比，返回现在的关号。
 *
 * 竞品价按关号存、和我方关卡数无关（Piggy 列配到 700 关），服务端插关也不挪它。所以：
 * - 新建的关本地网格是空的，不能当成「清空了」——下发空表会把那一关号上的竞品价整关删掉
 *   （10-28 1201 v172 新建 113–196 关，2100 格被清空）。新关只有人真填了才写
 * - 中间插关后后面的关本地重新编号、网格跟着关走；按关号比会把整列往后错一位写回去。按 uid 比，没动过就不写
 */
export function changedRivalLevels(base: Level[], next: Level[], rivalId: string): number[] {
  const out: number[] = [];
  for (const lv of next) {
    const mine = rivalCellsOf(lv, rivalId);
    const b = base.find((x) => x.uid === lv.uid);
    if (!b) { if (mine.length) out.push(lv.id); continue; }
    const theirs = rivalCellsOf(b, rivalId).map((c) => ({ ...c, level: lv.id }));
    if (!sameJson(theirs, mine)) out.push(lv.id);
  }
  return out;
}

/* ── 全局参数：cfg_main_common ↔ 骰子/对抗两页 ──
 * 表里是整数（bps 万分数、milli 千分数、秒、原值），页面是人读的单位。
 * 换算只在这一层；common 里页面没显示的列（diceCapMax/diceInit/pDouble 等）
 * 原样透传，PATCH 时不碰。 */

const div = (v: number | undefined, d: number): number | null => (v == null ? null : v / d);

export function toDice(c: ServerCommon | null): DiceParams {
  if (!c) return { ...DEFAULT_DICE };
  return {
    E: div(c.eTargetMilliB, 1000),
    rho: div(c.costRatioBps, 10000),
    unifiedB: Number(c.unifiedB ?? 0),
    incomeK: c.incomeGrowthBps ? c.incomeGrowthBps / 10000 : null,
    incomeAnchor: money(c.incomeAnchorB) || null,
    targetBase: c.targetRollsBase || null,
    targetGrowth: c.targetRollsGrowthBps ? c.targetRollsGrowthBps / 10000 : null,
    cap: c.diceCapBase ?? null,
    capStep: c.diceCapPerLevel ?? null,
    capStarStep: c.diceCapPerStar ?? null,
    regen: div(c.diceRegenSec, 60),
    pDouble: div(c.pDoubleBps, 10000),
    capMax: c.diceCapMax ?? null,
    init: c.diceInit ?? null,
    // -1 是新版本的「还没设置」占位：勾选框显示「还没设置」，而不是看着像「关」
    subgamePick: c.subgameClientPick == null || c.subgameClientPick < 0 ? null : c.subgameClientPick,
    // -1 是新版本的「还没填」占位：显示成空，让发布闸的 COMMON_UNFILLED 指回 spin 页
    warpProb: c.warpProbBps == null || c.warpProbBps < 0 ? null : c.warpProbBps / 100,
    // 救助金：-1 是新版本的「还没填」占位，显示成空；老版本没这一列（迁移默认 0）
    reliefQty: c.reliefDiceQty == null ? 0 : c.reliefDiceQty < 0 ? null : c.reliefDiceQty,
    reliefTimes: c.reliefDailyTimes == null ? 0 : c.reliefDailyTimes < 0 ? null : c.reliefDailyTimes,
    reliefThreshold: c.reliefDiceThreshold == null ? 0 : c.reliefDiceThreshold < 0 ? null : c.reliefDiceThreshold,
    // 挂机收益：新版本建出来就是 0（不开）；老版本没这两列同样按 0
    idlePct: c.idleTapPctBps == null ? 0 : c.idleTapPctBps < 0 ? null : c.idleTapPctBps / 10,
    // 上限 0 = 没填，运行时按下限：页面显示成下限，看着就是固定值
    idleMax: (c.idleTapMaxBps ?? 0) > 0 ? c.idleTapMaxBps / 10 : c.idleTapPctBps == null || c.idleTapPctBps < 0 ? 0 : c.idleTapPctBps / 10,
    idleCap: c.idleTapCapPerRoll == null ? 0 : c.idleTapCapPerRoll < 0 ? null : c.idleTapCapPerRoll,
  };
}

export function toFight(c: ServerCommon | null): FightParams {
  if (!c) return { ...DEFAULT_FIGHT };
  return {
    stealPct: div(c.stealPctBps, 10000),
    stealCap: c.stealCapB ?? null,
    stealFloor: c.stealFloorB ?? null,
    atkSuccess: div(c.atkSuccessBps, 10000),
    atkBlock: c.atkBlockB ?? null,
    protectNpc: c.protectNpcLevel ?? null,
    protectHalf: c.protectHalfLevel ?? null,
    giftCap: c.dailyGiftCap ?? null,
    stakeTierBase: c.stakeTierBase ?? null,
    askCap: c.dailyAskCap ?? null,
    wardCap: c.shieldMax ?? null,
    clawbackDays: c.luckyKeepDays ?? null,
    clawbackBonus: div(c.luckyBonusBps, 10000),
    repairPct: div(c.repairPctBps, 10000),
    visitCap: c.dailyStolenCap ?? null,
    matchRange: c.matchRangeLevels ?? null,
    thunderPct: div(c.thunderPctBps, 10000),
    thunderCap: c.thunderCapB ?? null,
    thunderFloor: c.thunderFloorB ?? null,
    friendLimit: c.friendLimit ?? null,
    socialEventCap: c.dailySocialEventCap ?? null,
    avatarUploadCap: c.dailyAvatarUploadCap ?? null,
    uidQueryCap: c.dailyUidQueryCap ?? null,
    puzzleHintCap: c.puzzleHintDailyLimit ?? null,
    cardLimit: c.dailyCardLimit ?? null,
    puzzleLimit: c.dailyPuzzleLimit ?? null,
    adLimit: c.dailyAdLimit ?? null,
  };
}

/** 萌宠小镇的四个全局参数。比例列按 bps 存，页面按百分比编 */
/** 宠物升星的五个全局参数。三个开关按 0 / 1 原样读，表单编成勾选框 */
export function toPetStar(c: ServerCommon | null): PetStarParams {
  if (!c) return { ...DEFAULT_PET_STAR };
  return {
    starMax: c.petStarMax ?? null,
    maxedCoin: money(c.petMaxedCoin),
    maxedApplyMultiplier: c.petMaxedApplyMultiplier ?? null,
    maxedCountsAsAdopt: c.petMaxedCountsAsAdopt ?? null,
  };
}

/** idol 亲密度的一个全局参数（cfg_main_common.intimacy_idol_daily_cap） */
export function toIntimacy(c: ServerCommon | null): IntimacyParams {
  if (!c) return { ...DEFAULT_INTIMACY };
  return { idolDailyCap: c.intimacyIdolDailyCap ?? null };
}

/** 神灯的一个全局参数（cfg_main_common.lamp_pity_threshold） */
export function toLamp(c: ServerCommon | null): LampParams {
  if (!c) return { ...DEFAULT_LAMP };
  return { pityThreshold: c.lampPityThreshold ?? null };
}

export function toPettown(c: ServerCommon | null): PettownParams {
  if (!c) return { ...DEFAULT_PETTOWN };
  return {
    maxStar: c.pettownMaxStar ?? null,
    starsPerLevel: c.pettownStarsPerLevel ?? null,
    repairPct: div(c.pettownRepairPctBps, 10000),
    attackerPct: div(c.pettownAttackerPctBps, 10000),
    protectStar: c.pettownProtectStar ?? null,
    matchRangeStars: c.pettownMatchRangeStars ?? null,
  };
}

/** 三页的差异合成一个 common patch；无差异返回 null */
export function diffCommon(
  base: { dice: DiceParams; fight: FightParams; pettown?: PettownParams; petStar?: PetStarParams; intimacy?: IntimacyParams; lamp?: LampParams },
  next: { dice: DiceParams; fight: FightParams; pettown?: PettownParams; petStar?: PetStarParams; intimacy?: IntimacyParams; lamp?: LampParams },
): Record<string, number | string> | null {
  const p: Record<string, number | string> = {};
  const put = (col: string, a: number | null, b: number | null, scale = 1) => {
    // 清空（null）不发：负数只是系统写入的「未填」占位，管端不许写（leo 定，config-content.ts patchCommon）。
    // 表单上会写明「清空不会存，原来的值还在」
    if (b != null && a !== b) p[col] = Math.round(b * scale);
  };
  // 金币量级的列没有上限：按 bigint 比、原样发（超过 2^53 是十进制字符串，服务端两种都收）
  const putMoney = (col: string, a: Money | null, b: Money | null) => {
    const nb = toBig(b);
    if (nb != null && nb !== toBig(a)) p[col] = fromBig(nb);
  };
  put("eTargetMilliB", base.dice.E, next.dice.E, 1000);
  put("costRatioBps", base.dice.rho, next.dice.rho, 10000);
  put("unifiedB", base.dice.unifiedB ?? 0, next.dice.unifiedB ?? 0);
  put("incomeGrowthBps", base.dice.incomeK ?? 0, next.dice.incomeK ?? 0, 10000);
  putMoney("incomeAnchorB", base.dice.incomeAnchor ?? 0, next.dice.incomeAnchor ?? 0);
  put("targetRollsBase", base.dice.targetBase ?? 0, next.dice.targetBase ?? 0);
  put("targetRollsGrowthBps", base.dice.targetGrowth ?? 0, next.dice.targetGrowth ?? 0, 10000);
  put("diceCapBase", base.dice.cap, next.dice.cap);
  put("diceCapPerLevel", base.dice.capStep, next.dice.capStep);
  put("diceCapPerStar", base.dice.capStarStep, next.dice.capStarStep);
  put("diceRegenSec", base.dice.regen, next.dice.regen, 60);
  put("pDoubleBps", base.dice.pDouble, next.dice.pDouble, 10000);
  put("diceCapMax", base.dice.capMax, next.dice.capMax);
  put("diceInit", base.dice.init, next.dice.init);
  put("subgameClientPick", base.dice.subgamePick, next.dice.subgamePick);
  put("warpProbBps", base.dice.warpProb, next.dice.warpProb, 100);
  put("reliefDiceQty", base.dice.reliefQty, next.dice.reliefQty);
  put("reliefDailyTimes", base.dice.reliefTimes, next.dice.reliefTimes);
  put("reliefDiceThreshold", base.dice.reliefThreshold, next.dice.reliefThreshold);
  put("idleTapPctBps", base.dice.idlePct, next.dice.idlePct, 10);
  put("idleTapMaxBps", base.dice.idleMax, next.dice.idleMax, 10);
  put("idleTapCapPerRoll", base.dice.idleCap, next.dice.idleCap);
  put("dailyCardLimit", base.fight.cardLimit, next.fight.cardLimit);
  put("dailyPuzzleLimit", base.fight.puzzleLimit, next.fight.puzzleLimit);
  put("dailyAdLimit", base.fight.adLimit, next.fight.adLimit);
  put("stealPctBps", base.fight.stealPct, next.fight.stealPct, 10000);
  put("stealCapB", base.fight.stealCap, next.fight.stealCap);
  put("shieldMax", base.fight.wardCap, next.fight.wardCap);
  put("luckyKeepDays", base.fight.clawbackDays, next.fight.clawbackDays);
  put("luckyBonusBps", base.fight.clawbackBonus, next.fight.clawbackBonus, 10000);
  put("repairPctBps", base.fight.repairPct, next.fight.repairPct, 10000);
  put("dailyStolenCap", base.fight.visitCap, next.fight.visitCap);
  put("matchRangeLevels", base.fight.matchRange, next.fight.matchRange);
  put("stealFloorB", base.fight.stealFloor, next.fight.stealFloor);
  put("atkSuccessBps", base.fight.atkSuccess, next.fight.atkSuccess, 10000);
  put("atkBlockB", base.fight.atkBlock, next.fight.atkBlock);
  put("thunderPctBps", base.fight.thunderPct, next.fight.thunderPct, 10000);
  put("thunderCapB", base.fight.thunderCap, next.fight.thunderCap);
  put("thunderFloorB", base.fight.thunderFloor, next.fight.thunderFloor);
  put("protectNpcLevel", base.fight.protectNpc, next.fight.protectNpc);
  put("protectHalfLevel", base.fight.protectHalf, next.fight.protectHalf);
  put("dailyGiftCap", base.fight.giftCap, next.fight.giftCap);
  put("stakeTierBase", base.fight.stakeTierBase, next.fight.stakeTierBase);
  put("dailyAskCap", base.fight.askCap, next.fight.askCap);
  put("friendLimit", base.fight.friendLimit, next.fight.friendLimit);
  put("dailySocialEventCap", base.fight.socialEventCap, next.fight.socialEventCap);
  put("dailyAvatarUploadCap", base.fight.avatarUploadCap, next.fight.avatarUploadCap);
  put("dailyUidQueryCap", base.fight.uidQueryCap, next.fight.uidQueryCap);
  put("puzzleHintDailyLimit", base.fight.puzzleHintCap, next.fight.puzzleHintCap);
  if (base.pettown && next.pettown) {
    put("pettownMaxStar", base.pettown.maxStar, next.pettown.maxStar);
    put("pettownStarsPerLevel", base.pettown.starsPerLevel, next.pettown.starsPerLevel);
    put("pettownRepairPctBps", base.pettown.repairPct, next.pettown.repairPct, 10000);
    put("pettownAttackerPctBps", base.pettown.attackerPct, next.pettown.attackerPct, 10000);
    put("pettownProtectStar", base.pettown.protectStar, next.pettown.protectStar);
    put("pettownMatchRangeStars", base.pettown.matchRangeStars, next.pettown.matchRangeStars);
  }
  /*
   * 宠物升星的五个全局参数（《宠物升星与满级规则 V2.0》§6）。三个布尔按 tinyint 存，
   * 所以走同一条数值 PATCH，1 / 0 传下去。
   *
   * ⚠️ 全部**当前不生效**：升星是 V2，玩家的宠物恒为 1★，够不到上限、触发不了满级。
   */
  if (base.petStar && next.petStar) {
    put("petStarMax", base.petStar.starMax, next.petStar.starMax);
    putMoney("petMaxedCoin", base.petStar.maxedCoin, next.petStar.maxedCoin);
    put("petMaxedApplyMultiplier", base.petStar.maxedApplyMultiplier, next.petStar.maxedApplyMultiplier);
    put("petMaxedCountsAsAdopt", base.petStar.maxedCountsAsAdopt, next.petStar.maxedCountsAsAdopt);
  }
  // idol 亲密度：单个 idol 每日上限（0 = 不限），与曲线 / 来源两张表同页、同一次顶栏保存
  if (base.lamp && next.lamp) {
    put("lampPityThreshold", base.lamp.pityThreshold, next.lamp.pityThreshold);
  }
  if (base.intimacy && next.intimacy) {
    put("intimacyIdolDailyCap", base.intimacy.idolDailyCap, next.intimacy.idolDailyCap);
  }
  return Object.keys(p).length ? p : null;
}

/* ── 奖励 ── */

const splitI18n = (name: string, i18n: Record<string, string>): Record<string, string> => ({
  en: name,
  ...Object.fromEntries(Object.entries(i18n).filter(([c, v]) => c !== "en" && v)),
});

export function toReward(sr: ServerReward): Reward {
  const i18n = Object.fromEntries(Object.entries(sr.nameI18n ?? {}).filter(([c, v]) => c !== "en" && v));
  return {
    id: sr.rewardId,
    buildingSystem: sr.buildingSystem ?? "levels",
    levelId: sr.level,
    name: sr.name,
    i18n,
    items: sr.items.map((i) => ({ kind: i.kind as RewardKind, ref: i.ref ?? null, qty: i.qty })),
  };
}

export const toServerReward = (r: Reward): Omit<ServerReward, "name"> => ({
  rewardId: r.id,
  buildingSystem: r.buildingSystem ?? "levels",
  level: r.levelId,
  nameI18n: splitI18n(r.name, r.i18n),
  items: r.items.map((i: RewardItem) => ({ kind: i.kind, ref: i.ref ?? null, qty: i.qty })),
});

/* ── 任务 / 成就 ── */

/** 成就的 money 项另带 q / sn / sd（共享奖励双字段）：原样带过去、带回来，不经小数；没有就不出现 */
const sharedQ = (i: { q?: string | null; sn?: string | null; sd?: string | null }) =>
  (i.q != null || i.sn != null || i.sd != null ? { q: i.q ?? null, sn: i.sn ?? null, sd: i.sd ?? null } : {});
const toItems = (items: Array<{ kind: string; ref?: string | null; qty: number | null; q?: string | null; sn?: string | null; sd?: string | null }> | undefined): RewardItem[] =>
  (items ?? []).map((i) => ({ kind: i.kind as RewardKind, ref: i.ref ?? null, qty: i.qty, ...sharedQ(i) }));
const fromItems = (items: RewardItem[]) => items.map((i) => ({ kind: i.kind, ref: i.ref ?? null, qty: i.qty, ...sharedQ(i) }));

export function toAchievement(sa: ServerAchievement): Achievement {
  const period = (["daily", "weekly", "lifetime", "newbie"].includes(sa.period) ? sa.period : "lifetime") as Achievement["period"];
  return {
    id: sa.achievementId,
    period,
    kind: (sa.kind === "track" ? "track" : "task"),
    hidden: sa.hidden === true,
    window: (["period", "daily", "weekly", "lifetime"].includes(sa.window) ? sa.window : "period") as Achievement["window"],
    metric: sa.metric,
    points: Number(sa.points ?? 0),
    weight: Number(sa.weight ?? 0),
    mustShow: sa.mustShow === true,
    replaceWhen: sa.replaceWhen ?? null,
    newbieDay: sa.newbieDay ?? null,
    params: sa.params ?? null,
    title: { ...(sa.titleI18n ?? {}) },
    desc: { ...(sa.descI18n ?? {}) },
    icon: sa.icon ?? "",
    jumpTo: sa.jumpTo ?? "",
    category: sa.category ?? "",
    sortOrder: sa.sortOrder ?? 0,
    enabled: sa.enabled !== false,
    stages: (sa.stages ?? []).map((s, i) => ({
      stage: Number(s.stage ?? i + 1),
      label: (s.label_i18n ?? {}) as I18nText,
      desc: (s.desc_i18n ?? {}) as I18nText,
      target: s.target ?? null,
      items: toItems(s.items),
      byLevel: (s.by_level ?? []).map((b) => ({ minLevel: b.min_level ?? null, items: toItems(b.items) })),
      byStar: s.by_star == null ? null : s.by_star.map((b) => ({ minLevel: b.min_star ?? null, items: toItems(b.items) })),
    })),
  };
}

export const toServerAchievement = (a: Achievement): Omit<ServerAchievement, "title"> => ({
  achievementId: a.id,
  period: a.period,
  kind: a.kind,
  window: a.window,
  metric: a.metric,
  points: a.points,
  weight: a.weight,
  mustShow: a.mustShow,
  // 没配条件替换时发 null，与「不替换」同义
  replaceWhen: a.replaceWhen?.cond && a.replaceWhen.with ? a.replaceWhen : null,
  newbieDay: a.period === "newbie" ? a.newbieDay : null,
  hidden: a.hidden,
  params: a.params && Object.keys(a.params).length ? a.params : null,
  titleI18n: Object.fromEntries(Object.entries(a.title).filter(([, v]) => v)),
  descI18n: Object.fromEntries(Object.entries(a.desc).filter(([, v]) => v)),
  icon: a.icon,
  jumpTo: a.jumpTo,
  category: a.category,
  sortOrder: a.sortOrder,
  enabled: a.enabled,
  stages: a.stages.map((s) => ({
    stage: s.stage,
    target: s.target,
    items: fromItems(s.items),
    // 名字 / 说明都是可选：一个语言都没填就发 null，与「没配」同义
    label_i18n: Object.values(s.label).some(Boolean) ? Object.fromEntries(Object.entries(s.label).filter(([, v]) => v)) : null,
    desc_i18n: Object.values(s.desc).some(Boolean) ? Object.fromEntries(Object.entries(s.desc).filter(([, v]) => v)) : null,
    // 空分段不发 by_level，服务端与「所有玩家一样」同义
    by_level: s.byLevel.length ? s.byLevel.map((b) => ({ min_level: b.minLevel, items: fromItems(b.items) })) : null,
    by_star: s.byStar == null ? null : s.byStar.map((b) => ({ min_star: b.minLevel, items: fromItems(b.items) })),
  })),
});

export const toTitle = (st: ServerTitle): Title => ({
  id: st.titleId, name: { ...(st.nameI18n ?? {}) }, icon: st.icon ?? "",
  sortOrder: st.sortOrder ?? 0, enabled: st.enabled !== false,
});
export const toServerTitle = (t: Title): Omit<ServerTitle, "name"> => ({
  titleId: t.id,
  nameI18n: Object.fromEntries(Object.entries(t.name).filter(([, v]) => v)),
  icon: t.icon, sortOrder: t.sortOrder, enabled: t.enabled,
});

export const toActItem = (s: ServerActItem): ActItem => ({
  ref: s.ref, name: { ...(s.nameI18n ?? {}) }, desc: { ...(s.descI18n ?? {}) }, icon: s.icon ?? "",
  sortOrder: s.sortOrder ?? 0, enabled: s.enabled !== false,
});
export const toServerActItem = (a: ActItem): Omit<ServerActItem, "name"> => ({
  ref: a.ref,
  nameI18n: Object.fromEntries(Object.entries(a.name).filter(([, v]) => v)),
  descI18n: Object.fromEntries(Object.entries(a.desc).filter(([, v]) => v)),
  icon: a.icon, sortOrder: a.sortOrder, enabled: a.enabled,
});

/* ── 卡包 ── */

export function toPack(sp: ServerPack): CardPack {
  const i18n = Object.fromEntries(Object.entries(sp.nameI18n ?? {}).filter(([c, v]) => c !== "en" && v));
  const cards = (sp.cards as Card[]) ?? [];
  return {
    id: sp.packId,
    code: sp.code,
    name: sp.name,
    i18n,
    count: sp.count ?? null,
    cover: sp.cover,
    coverImg: sp.coverImg ?? "",
    cards,
    disabled: sp.disabled || undefined,
    wildcardOk: sp.wildcardOk || undefined,
    weight: sp.weight ?? null,
    // bps → 百分数字符串（UI 编辑态）。整数百分比不带小数点，非整保留原精度
    drops: (sp.drops ?? []).map((d, i) => ({
      id: `dp${i}`, level: d.level, rate: String(d.rateBps % 100 === 0 ? d.rateBps / 100 : d.rateBps / 100),
    })),
    starDrops: sp.starDrops == null ? null : sp.starDrops.map((d, i) => ({ id: `sp${i}`, level: d.star, rate: String(d.rateBps / 100) })),
    ...(sp.kind === "idol"
      ? { unlockPack: sp.unlockPack, idolId: sp.idolId || null, shopDrawCount: sp.shopDrawCount ?? 6, shopPriceGem: sp.shopPriceGem ?? 0 }
      : { setReward: sp.setReward ?? "", setRewards: (sp.setRewards ?? []).map((i) => ({ kind: i.kind as RewardItem["kind"], ref: i.ref ?? null, qty: i.qty })) }),
  };
}

export const toServerPack = (p: CardPack, kind: "item" | "idol"): Omit<ServerPack, "name"> => ({
  packId: p.id,
  kind,
  code: p.code,
  nameI18n: splitI18n(p.name, p.i18n),
  cover: p.cover,
  coverImg: p.coverImg ?? "",
  disabled: !!p.disabled,
  setReward: p.setReward ?? null,
  setRewards: kind === "item" ? (p.setRewards ?? []).map((i) => ({ kind: i.kind, ref: i.ref ?? null, qty: i.qty })) : null,
  shopDrawCount: kind === "idol" ? (p.shopDrawCount ?? 6) : null,
  shopPriceGem: kind === "idol" ? (p.shopPriceGem ?? 0) : null,
  unlockPack: p.unlockPack ?? null,
  idolId: kind === "idol" ? (p.idolId || null) : null,
  count: p.count ?? null,
  wildcardOk: !!p.wildcardOk,
  weight: p.weight ?? null,
  // 百分数 → bps。空概率的行不落库（半行配置没有意义，落了运行时还得防）
  drops: (p.drops ?? [])
    .filter((d) => d.rate !== "" && Number(d.rate) > 0)
    .map((d) => ({ level: d.level, rateBps: Math.round(Number(d.rate) * 100) })),
  starDrops: null, // 保存统一等级配置时清除旧逐星数据，不把星数自动当作等级
  cards: p.cards,
});

/* ── 竞拍 ── */

export function toAuctions(byLevel: Record<string, ServerPeriod[]>): AuctionMap {
  const out: AuctionMap = {};
  for (const [lv, periods] of Object.entries(byLevel)) {
    out[Number(lv)] = [...periods]
      .sort((a, b) => a.seq - b.seq)
      .map((p) => ({
        id: `ac-${lv}-${p.seq}`,
        bidStartMs: p.bidStartMs,
        bidEndMs: p.bidEndMs,
        minInc: div(p.minIncBps ?? undefined, 10000),
        sparkBonus: div(p.sparkBonusBps ?? undefined, 10000),
        enabled: p.enabled,
      }));
  }
  return out;
}

export const toServerPeriods = (periods: AuctionPeriod[]): Omit<ServerPeriod, "seq">[] =>
  periods.map((p) => ({
    bidStartMs: p.bidStartMs,
    bidEndMs: p.bidEndMs,
    minIncBps: p.minInc == null ? null : Math.round(p.minInc * 10000),
    sparkBonusBps: p.sparkBonus == null ? null : Math.round(p.sparkBonus * 10000),
    enabled: p.enabled,
  }));

/**
 * 识别「全部关卡各追加了同一期」：每关期数 +1、原有各期原样、新末期各关相同。
 * 命中则整个改动可以用一次 new-period 表达；否则返回 null 走逐关替换。
 */
export function detectBulkAppend(
  base: AuctionMap,
  next: AuctionMap,
  levels: Level[],
): Omit<ServerPeriod, "seq" | "enabled"> | null {
  if (!levels.length) return null;
  let appended: Omit<ServerPeriod, "seq">[] | null = null;
  for (const l of levels) {
    const bp = base[l.id] ?? [], np = next[l.id] ?? [];
    if (np.length !== bp.length + 1) return null;
    if (JSON.stringify(np.slice(0, bp.length)) !== JSON.stringify(bp)) return null;
    const last = toServerPeriods([np[np.length - 1]]);
    if (!last[0].enabled) return null;
    if (appended == null) appended = last;
    else if (JSON.stringify(appended) !== JSON.stringify(last)) return null;
  }
  if (!appended) return null;
  const { enabled: _e, ...period } = appended[0];
  return period;
}
